import { z } from 'zod';

import {
  contractCustomDetailEntrySchema,
  ContractTypeValues,
  createContractAttachmentSchema,
} from '~/data/schemas/contract';

const contractReviewRulesSchema = z.object({
  id: z.string().optional(),
  reviewRule: z.string().min(1, 'Review rule is required'),
  category: z
    .enum([
      'review-rule',
      'included-service',
      'excluded-service',
      'miscellaneous-charge',
      'other-term',
    ])
    .default('review-rule'),
});

const propertyContractFormSchema = z.object({
  id: z.string().optional(),
  propertyId: z.string(),
  startDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  rateSetId: z.string().nullable(),
  monthlyPrice: z.string(),
  cars: z.array(
    z.object({
      id: z.string().optional(),
      carId: z.string(),
      name: z.string().readonly(),
      ahjId: z.string().readonly(),
      elevatorType: z.string().readonly(),
      maintenanceQuantity: z.coerce.number(),
      maintenanceUnit: z.enum(['visits', 'hours']),
      maintenanceTimePeriod: z.coerce.number(),
      regularCallbackResponseTime: z.coerce.number(),
      emergencyCallbackResponseTime: z.coerce.number(),
      regularCallbackRate: z.coerce.number(),
      emergencyCallbackRate: z.coerce.number(),
    }),
  ),
});

const trueFalseOrUnknown = z
  .union([z.enum(['true', 'false', 'unknown']), z.boolean(), z.null()])
  .transform((val) => {
    if (typeof val === 'boolean') return val;
    if (val === 'true') return true;
    if (val === 'false') return false;
    return null;
  });

const rateSchema = z.object({
  id: z.string().optional(),
  rateType: z.string().min(1, 'Rate type is required'),
  workerType: z.string().min(1, 'Worker type is required'),
  workType: z.string().min(1, 'Work type is required'),
  hourlyRate: z.coerce
    .number()
    .gt(0, 'Rate must be greater than 0')
    .transform((val) => (isNaN(val) ? 0 : val)),
  coverageType: z.string().default('billable'),
  isDefault: trueFalseOrUnknown.default('false'),
  description: z.string().optional().default(''),
});

// Define schema for rate set
const rateSetSchema = z.object({
  id: z.string().optional(),
  isNew: z.boolean().default(false),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional().default(''),
  rates: z.array(rateSchema).optional().default([]),
});

export const ContractForm = z.object({
  action: z.enum(['create', 'update']),
  id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  // The backend FK is nullable (blank=False, null=True) and the UI offers a
  // "None" option — an empty string is a legitimate "no provider" value, not
  // a missing one, and legacy contracts can carry it too.
  serviceProvider: z.string(),
  attachments: z.array(createContractAttachmentSchema).optional(),
  startDate: z.string(),
  expirationDate: z.string(),
  term: z.string().min(1, 'Term is required'),
  type: z.enum(ContractTypeValues),
  contractPaper: z.string().min(1, 'Contract paper is required'),
  // Both are blank=True, null=True on the backend; legacy contracts can carry
  // a genuinely empty value here.
  businessHours: z.string(),
  cancellation: z.string(),
  partsAndMaterialsMarkup: z.coerce
    .number()
    .min(0, 'Parts and materials markup must be greater than 0'),
  hourlyFeeAdjustmentRate: z.coerce
    .number()
    .min(0, 'Hourly fee adjustment rate must be greater than 0'),
  phoneMonitoring: trueFalseOrUnknown.nullable(),
  fireService: trueFalseOrUnknown.nullable(),
  pressureTest: trueFalseOrUnknown.nullable(),
  notes: z.string().default(''),
  customDetails: z.array(contractCustomDetailEntrySchema).default([]),
  rateSets: z.array(rateSetSchema),
  reviewRules: z.array(contractReviewRulesSchema),
  propertyContract: propertyContractFormSchema,
});

// The parsed result: what `handleSubmit` hands the submit callback.
export type ContractForm = z.infer<typeof ContractForm>;

// What the form itself holds. `notes` and `customDetails` carry `.default()`
// and the two markup fields are `z.coerce.number()`, so the values in the
// fields while the user is editing are not the parsed values above.
export type ContractFormValues = z.input<typeof ContractForm>;
